import { Container, Divider, Stack } from '@mui/material';

import Button from 'components/Common/Button';
import Tab from 'components/Common/Tab';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import routes from 'constants/routes';
import ActivityContainer from 'components/Common/ActivityContainerTable';
import ParishonerProfileContainer from 'components/Community/ParishonerProfileContainer';
import MenuList from 'components/Common/MenuList';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, store } from 'redux/store';
import { fetchWalletActivity } from 'redux/slices/wallet/walletActivitySlice';
import MuiLoader from 'components/Common/Loader';
import { dateFormat, formatAmount, getTodayFilter, timeFormat } from 'utils/helpers';
import { defaultOffset, tablePageSize } from 'appConfig';
import DownloadCSV from 'components/CSV/DownloadCSV';

interface ParishonerWalletProps {
    type?: string;
}

const columns = [
    { id: 'transactionDate', label: 'Date', render: ({ data }: any) => dateFormat(data.transactionDateTime) },
    { id: 'transactionTime', label: 'Time', render: ({ data }: any) => timeFormat(data.transactionDateTime, 'hh:mm A') },
    { id: 'companyName', label: 'From', render: ({ data }: any) => data?.company?.name },
    { id: 'event.username', label: 'Username', render: ({ data }: any) => data?.consumer?.name },
    { id: 'transactionStatus', label: 'Status' },
    { id: 'amount', label: 'Total', render: ({ value }: any) => formatAmount(value) },
];

const textInputPlaceholders = ['Total Received:', 'Total Pending:', 'Search Here'];

const csvHeaders = [
    { label: 'Date', key: 'date' },
    { label: 'Time', key: 'time' },
    { label: 'From', key: 'company.name' },
    { label: 'Username', key: 'consumer.username' },
    { label: 'Total', key: 'amount' },
];

function ParishonerWallet({ type }: ParishonerWalletProps) {
    const [tab, setTab] = useState('');
    const dispatch = useDispatch<typeof store.dispatch>();
    const { walletActivity, balance, logoUrl, tableState, isMerchant } = useSelector((state: RootState) => ({
        walletActivity: state.wallet.walletActivity,
        balance: state.wallet.merchantBalance.balance,
        logoUrl: state.user.defaultMerchant?.logoUrl,
        tableState: state.app.table,
        isMerchant: state?.auth?.isMerchant,
    }));

    const { pageNumber, ...rest } = tableState;
    const [filter, setFilter] = useState<any>({ ...getTodayFilter(), ...rest });
    const { walletActivityData, loading } = walletActivity || {};

    useEffect(() => {
        dispatch(fetchWalletActivity(filter));
    }, [filter, dispatch]);

    const setTabValue = (value: string) => {
        setTab(value);
    };

    const navigationHandler = (row: any) => {
        navigate(routes.transaction.main.replace(':id', row?.confirmationCode?.toString()));
    };

    let navigate = useNavigate();

    return (
        <>
            <MuiLoader absolute loading={loading} />
            <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent={'space-evenly'}>
                <div>
                    <Container>
                        <ParishonerProfileContainer
                            imgUrl={logoUrl}
                            title="Available Balance"
                            subtitle={formatAmount(balance)}
                            showIcon={true}
                        />
                    </Container>

                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12, marginBottom: 12 }}>
                        <Button
                            onClick={() => navigate(routes.wallet.withdrawToBank)}
                            width={'23rem'}
                            title={'Withdraw or Transfer balance'}
                            disabled={!isMerchant}
                        />
                    </div>
                </div>
                <Container sx={{ backgroundColor: 'white', p: 1, mx: 2 }}>
                    <Stack direction="row" justifyContent="space-between">
                        <Tab tabs={[{ value: 'activity', label: 'Activity' }]} tabValue={tab} setTabValue={setTabValue} />
                        <MenuList
                            mt={2}
                            menuItems={[
                                {
                                    item: 'Export to CSV',
                                    render: () => <DownloadCSV data={walletActivityData} headers={csvHeaders} fileName="Wallet-activity" />,
                                    disabled: !walletActivityData?.length,
                                },
                            ]}
                        />
                    </Stack>
                    <Divider light={true} />
                    <ActivityContainer
                        navigationHandler={navigationHandler}
                        records={walletActivityData}
                        columns={columns}
                        textInputPlaceholders={textInputPlaceholders}
                        setFilter={setFilter}
                        filter={filter}
                        serverSidePagination
                        defaultPagination={false}
                        loading={loading}
                    />
                </Container>
            </Stack>
        </>
    );
}

export default ParishonerWallet;
